/**
@format
@flow
*/

import React from 'react';
import styled from 'styled-components';
import Icon from '@ziynx/components/icons';
import { Body } from '@ziynx/components/typography';
import Colors from '@ziynx/theme/colors';
import type { IconType } from '@ziynx/flow-types/icons';
import ListItemWrapper from './ListItemWrapper';

export type Props = $ReadOnly<{|
    /**
     * Icon for ListItemIcon
     */
    icon?: IconType,
    /**
     * Optional Label
     */
    label?: {
      text?: string,
      labelPosition?: 'bottom' | 'top',
      color?: string,
    },
    /**
     * Text for list item
     */
    text?: string,
    /**
     * Children passed are text as well
     */
    children?: any,
    /**
     * If text is a password it can be hidden with this prop
     */
    password?: boolean,
    /**
     * Color for text
     */
    color?: string,
    /**
     * Width
     */
    width?: string | number,
    /**
     * Max-width
     */
    maxWidth?: string | number,

|}>;

const Wrapper = styled(ListItemWrapper)`
  ${({ label }) => label && 'min-width: 168px'};
  max-width: ${({ label }) => (label ? '640px' : '688px')};
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 16px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StyledBody = styled(Body)`
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ label }) => label && 'white-space: nowrap;'};
  ${({ label }) => !label
    && 'display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; max-height: 44px;'};    
  ${({ password }) => password && '-webkit-text-security: disc'};
`;

const StyledBodyTop = styled(StyledBody)`
  margin-bottom: 4px;
`;

const ListItemIcon = (props: Props): React$Node => {
  const {
    icon,
    label,
    text,
    password,
    color,
    width,
    maxWidth,
    children,
    ...rest
  } = props;

  const iconSize: string = icon?.size || 'veryTiny';

  return (
    <Wrapper
      alignment="center"
      width={width}
      maxWidth={maxWidth}
      label={label}
      {...rest}
    >
      {icon && (
        <IconWrapper>
          <Icon
            name={icon.name}
            color={icon?.color || Colors.PrimaryBlue}
            {...{ [iconSize]: true }}
          />
        </IconWrapper>
      )}
      <ContentWrapper>
        {label && (
          <StyledBodyTop label={label} medium color={label?.color || Colors.GrayDark}>
            {label?.text}
          </StyledBodyTop>
        )}
        <StyledBody label={label} password={password} medium color={color}>
          {text}
          {children}
        </StyledBody>
      </ContentWrapper>
    </Wrapper>
  );
};

ListItemIcon.defaultProps = {
  color: Colors.GrayVeryDark,
};

export default ListItemIcon;
